// ============================================================================
// ENTITY (COMPANY) HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { entityService } from 'api/services/entityService';
import type { Entity, CreateEntityRequest, UpdateEntityRequest, EntityFilterParams } from 'api/types/entity.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'entities';

export const useEntities = (params?: EntityFilterParams) => {
  const [data, setData] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await entityService.getAll({ pageSize: 50000, ...params });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useDataChangeListener(DATA_KEY, fetchEntities);

  return { data, loading, error, refetch: fetchEntities };
};

export const useCreateEntity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createEntity = useCallback(async (payload: CreateEntityRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await entityService.create(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createEntity, loading, error };
};

export const useUpdateEntity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateEntity = useCallback(async (id: string, payload: UpdateEntityRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await entityService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateEntity, loading, error };
};

export const useDeleteEntity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteEntity = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await entityService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteEntity, loading, error };
};
